import { useQuery, type QueryClient } from '@tanstack/react-query';
import type { BitrixConnection, BitrixOpenlines } from '@chatapp-inbox/shared';
import { getBitrix, getBitrixOpenlines, getBitrixOptions, getBitrixUserLinks } from '../../api';
import { bitrixKey, bitrixOpenlinesKey, bitrixOptionsKey, bitrixUserLinksKey } from './keys';

/** Enquanto espera a instalação no Bitrix, a tela pergunta de novo a cada tanto e vira "Conectado" sozinha. */
export const INSTALL_POLL_MS = 4000;

export function useBitrixConnection() {
  return useQuery({
    queryKey: bitrixKey,
    queryFn: ({ signal }) => getBitrix(signal),
    refetchInterval: (query) =>
      (query.state.data as BitrixConnection | undefined)?.status === 'awaiting_install'
        ? INSTALL_POLL_MS
        : false,
  });
}

/** Usuários, etapas e origens do Bitrix: só com a conexão pronta (antes, o servidor responde 409). */
export function useBitrixOptions(enabled: boolean) {
  return useQuery({
    queryKey: bitrixOptionsKey,
    queryFn: ({ signal }) => getBitrixOptions(signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Registra o estado novo (resposta de salvar). As escolhas do Bitrix ficam obsoletas se a conexão mudou. */
export function applyBitrix(queryClient: QueryClient, connection: BitrixConnection): void {
  queryClient.setQueryData(bitrixKey, connection);
  void queryClient.invalidateQueries({ queryKey: bitrixOptionsKey });
}

/** O Canal Aberto: só com a conexão pronta (antes, o servidor responde 409 ao ligar). */
export function useBitrixOpenlines() {
  return useQuery({
    queryKey: bitrixOpenlinesKey,
    queryFn: ({ signal }) => getBitrixOpenlines(signal),
  });
}

export function applyOpenlines(queryClient: QueryClient, openlines: BitrixOpenlines): void {
  queryClient.setQueryData(bitrixOpenlinesKey, openlines);
}

/** Os vínculos entre usuários do CladvApp e do Bitrix. */
export function useBitrixUserLinks(enabled: boolean) {
  return useQuery({
    queryKey: bitrixUserLinksKey,
    queryFn: ({ signal }) => getBitrixUserLinks(signal),
    enabled,
  });
}
